import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { ArrowRight, ArrowDown, CheckCircle, Play, FilePdf, MapPin } from "@phosphor-icons/react";
import { LEGAL_LINKS } from "../../lib/legal";

const NIGHT_BG = "https://static.prod-images.emergentagent.com/jobs/642600d4-b2ad-4c29-8e61-9c3e489676b3/images/3843ed6defac8ee609de6f9b5f086e6b153070c04da755a59bcd517962ba0227.jpeg";

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.7, ease: "easeOut" },
};

const Overline = ({ children, light }) => (
    <div className={`lp-mono text-[10px] tracking-[0.28em] mb-5 ${light ? "text-emerald-700" : "text-[#00E599]"}`}>{children}</div>
);

export const CountUp = ({ value, suffix = "", prefix = "", duration = 1100 }) => {
    const [n, setN] = useState(0);
    const ref = useRef(null);
    const started = useRef(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(([e]) => {
            if (!e.isIntersecting || started.current) return;
            started.current = true;
            const t0 = performance.now();
            const tick = (t) => {
                const p = Math.min((t - t0) / duration, 1);
                setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
                if (p < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
        }, { threshold: 0.4 });
        obs.observe(el);
        return () => obs.disconnect();
    }, [value, duration]);
    return <span ref={ref}>{prefix}{n.toLocaleString("it-IT")}{suffix}</span>;
};

/* ---------------- FUNNEL ---------------- */
const FUNNEL = [
    { label: "ITALIA", desc: "20 regioni, tutti i tribunali." },
    { label: "ASTE MONITORATE", desc: "Portali pubblici scansionati ogni giorno." },
    { label: "FILTRO RADAR", desc: "Pre-screening €/m² rispetto al comune." },
    { label: "ANALISI DOCUMENTALE", desc: "Perizia, occupazione, vincoli, difformità." },
    { label: "INVESTMENT ENGINE", desc: "Costi, tasse, utile netto: calcolo deterministico." },
    { label: "MIGLIORI OPPORTUNITÀ", desc: "Solo le aste con un margine reale.", final: true },
];

export const LandingFunnel = () => (
    <section className="relative bg-[#050505] py-24 sm:py-32 lp-grain overflow-hidden" data-testid="landing-funnel">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 grid lg:grid-cols-[1fr_1fr] gap-14 items-start">
            <div className="lg:sticky lg:top-28">
                <Overline>IL PROBLEMA NON È TROVARE ASTE. È SCEGLIERLE.</Overline>
                <motion.h2 {...fadeUp} className="lp-display font-extrabold text-white text-4xl sm:text-5xl leading-[1.02] tracking-tight">
                    Dalle migliaia di aste<br />
                    <span className="text-white/45">a quelle che meritano davvero attenzione.</span>
                </motion.h2>
                <motion.p {...fadeUp} className="lp-body text-white/55 text-base leading-relaxed mt-6 max-w-[420px]">
                    Non cerchi le aste una per una. Il sistema le trova, le filtra
                    e le analizza. Tu vedi solo quelle che contano.
                </motion.p>
            </div>
            <div className="flex flex-col">
                {FUNNEL.map((s, i) => (
                    <motion.div key={s.label} initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.55, delay: i * 0.06 }}>
                        <div className={`rounded-xl border px-6 py-5 flex items-center gap-5 ${s.final
                            ? "border-[#00E599]/40 bg-[#00E599]/[0.06]"
                            : "border-white/[0.09] bg-white/[0.025]"}`}
                             data-testid={`funnel-step-${i}`}>
                            <span className={`lp-mono text-[10px] w-6 ${s.final ? "text-[#00E599]" : "text-white/30"}`}>{String(i + 1).padStart(2, "0")}</span>
                            <div>
                                <div className={`lp-mono text-[12px] tracking-[0.18em] font-bold ${s.final ? "text-[#00E599]" : "text-white/90"}`}>{s.label}</div>
                                <div className="lp-body text-[13px] text-white/45 mt-0.5">{s.desc}</div>
                            </div>
                        </div>
                        {i < FUNNEL.length - 1 && (
                            <div className="flex justify-center py-1.5">
                                <ArrowDown size={13} className="text-white/25" />
                            </div>
                        )}
                    </motion.div>
                ))}
            </div>
        </div>
    </section>
);

/* ---------------- RADAR ---------------- */
const RADAR_ROWS = [
    { city: "Firenze · Isolotto", type: "Appartamento · 92 mq", price: "€118.000", score: 87, roi: "22,8%", net: "€31.400" },
    { city: "Prato · Centro", type: "Trilocale · 78 mq", price: "€84.000", score: 79, roi: "18,1%", net: "€19.700" },
    { city: "Livorno · Ardenza", type: "Bilocale · 55 mq", price: "€61.500", score: 72, roi: "15,4%", net: "€12.300" },
];

export const LandingRadar = () => (
    <section id="radar" className="relative bg-[#0A0A0A] py-24 sm:py-32 border-t border-white/[0.06] scroll-mt-14 lp-grain overflow-hidden" data-testid="landing-radar">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 grid lg:grid-cols-[0.9fr_1.1fr] gap-14 items-center">
            <div>
                <Overline>RADAR NAZIONALE</Overline>
                <motion.h2 {...fadeUp} className="lp-display font-extrabold text-white text-4xl sm:text-5xl leading-[1.02] tracking-tight">
                    Il Radar cerca.<br /><span className="text-white/45">Tu scegli dove investire.</span>
                </motion.h2>
                <motion.p {...fadeUp} className="lp-body text-white/55 text-base leading-relaxed mt-6 max-w-[420px]">
                    Imposti le aree da monitorare. Il sistema scansiona i portali pubblici
                    ogni giorno, deduplica le aste e assegna a ciascuna un punteggio.
                    Quando compare un'occasione, la trovi già ordinata per margine.
                </motion.p>
                <motion.div {...fadeUp} className="lp-mono flex items-center gap-2 mt-8 text-[10px] tracking-[0.16em] text-white/40">
                    <MapPin size={13} className="text-[#00E599]" /> AREE MONITORATE: LE DECIDI TU
                </motion.div>
            </div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.8 }}
                        className="rounded-2xl border border-white/[0.1] bg-[#0F0F0F] overflow-hidden shadow-[0_30px_100px_rgba(0,0,0,0.55)]">
                <div className="flex items-center justify-between px-5 h-11 border-b border-white/[0.07]">
                    <span className="lp-mono text-[10px] tracking-[0.2em] text-white/50">RADAR · OPPORTUNITÀ</span>
                    <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#00E599] animate-pulse" /><span className="lp-mono text-[9px] text-white/35">SCANSIONE ATTIVA</span></span>
                </div>
                <div className="divide-y divide-white/[0.05]">
                    {RADAR_ROWS.map((r, i) => (
                        <motion.div key={r.city} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
                                    transition={{ delay: 0.25 + i * 0.18 }}
                                    className="px-5 py-4 flex items-center gap-4 hover:bg-white/[0.025] transition-colors" data-testid={`radar-row-${i}`}>
                            <div className={`lp-mono w-11 h-11 shrink-0 rounded-lg border flex items-center justify-center text-[15px] font-bold ${r.score >= 85 ? "border-[#00E599]/40 text-[#00E599] bg-[#00E599]/[0.07]" : "border-white/15 text-white/80"}`}>
                                {r.score}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="lp-body text-[14px] font-semibold text-white truncate">{r.city}</div>
                                <div className="lp-body text-[12px] text-white/40">{r.type}</div>
                            </div>
                            <div className="hidden sm:block text-right">
                                <div className="lp-mono text-[13px] text-white/85">{r.price}</div>
                                <div className="lp-mono text-[10px] text-white/35">PREZZO ASTA</div>
                            </div>
                            <div className="text-right">
                                <div className="lp-mono text-[13px] font-bold text-[#00E599]">{r.net}</div>
                                <div className="lp-mono text-[10px] text-white/35">UTILE · ROI {r.roi}</div>
                            </div>
                        </motion.div>
                    ))}
                </div>
                <div className="px-5 py-2.5 border-t border-white/[0.06]">
                    <span className="lp-mono text-[8px] tracking-[0.14em] text-white/25">DATI DIMOSTRATIVI</span>
                </div>
            </motion.div>
        </div>
    </section>
);

/* ---------------- ENGINE ---------------- */
const ENGINE_METRICS = [
    { label: "MAXIMUM BID", value: 127500, prefix: "€" },
    { label: "VALORE RIVENDITA", value: 184000, prefix: "€" },
    { label: "COSTO TOTALE", value: 152600, prefix: "€" },
    { label: "UTILE NETTO POST-TASSE", value: 31400, prefix: "€", accent: true },
];

export const LandingEngine = () => (
    <section id="analisi" className="relative bg-[#050505] py-24 sm:py-32 border-t border-white/[0.06] scroll-mt-14 lp-grain overflow-hidden" data-testid="landing-engine">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10">
            <div className="max-w-[620px]">
                <Overline>INVESTMENT ENGINE</Overline>
                <motion.h2 {...fadeUp} className="lp-display font-extrabold text-white text-4xl sm:text-5xl leading-[1.02] tracking-tight">
                    Prima di offrire,<br /><span className="text-white/45">conosci i numeri.</span>
                </motion.h2>
                <motion.p {...fadeUp} className="lp-body text-white/55 text-base leading-relaxed mt-6">
                    Quanto puoi offrire senza distruggere il margine? L'Engine lo calcola
                    in modo deterministico: acquisto, tasse, ristrutturazione, tempi di
                    rivendita. Nessuna stima improvvisata.
                </motion.p>
            </div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.8 }}
                        className="mt-12 rounded-2xl border border-white/[0.1] bg-[#0F0F0F] p-7 sm:p-9">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                    <div className="flex items-center gap-3">
                        <span className="lp-mono text-[10px] tracking-[0.2em] text-white/45">DECISIONE ENGINE</span>
                        <div className="flex gap-1.5" data-testid="engine-verdict">
                            <span className="lp-mono text-[10px] font-bold tracking-[0.12em] px-3 py-1 rounded bg-[#00E599]/[0.12] border border-[#00E599]/45 text-[#00E599]">BUY</span>
                            <span className="lp-mono text-[10px] tracking-[0.12em] px-3 py-1 rounded border border-white/10 text-white/25">REVIEW</span>
                            <span className="lp-mono text-[10px] tracking-[0.12em] px-3 py-1 rounded border border-white/10 text-white/25">REJECT</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-6">
                        <div className="text-right">
                            <div className="lp-mono text-[28px] font-bold text-white leading-none"><CountUp value={87} />/100</div>
                            <div className="lp-mono text-[9px] tracking-[0.2em] text-white/35 mt-1">INVESTMENT SCORE</div>
                        </div>
                        <div className="text-right">
                            <div className="lp-mono text-[28px] font-bold text-[#00E599] leading-none">22,8%</div>
                            <div className="lp-mono text-[9px] tracking-[0.2em] text-white/35 mt-1">ROI POST-TASSE</div>
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.06] rounded-xl overflow-hidden border border-white/[0.06]">
                    {ENGINE_METRICS.map((m) => (
                        <div key={m.label} className="bg-[#0A0A0A] p-5" data-testid={`engine-metric-${m.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                            <div className={`lp-mono text-[20px] sm:text-[24px] font-bold tracking-tight ${m.accent ? "text-[#00E599]" : "text-white"}`}>
                                <CountUp value={m.value} prefix={m.prefix} />
                            </div>
                            <div className="lp-mono text-[9px] tracking-[0.18em] text-white/35 mt-1.5">{m.label}</div>
                        </div>
                    ))}
                </div>
                <div className="lp-mono text-[8px] tracking-[0.14em] text-white/25 mt-4">ESEMPIO DIMOSTRATIVO · OGNI ANALISI USA I DATI REALI DELL'ASTA</div>
            </motion.div>
        </div>
    </section>
);

/* ---------------- WATERFALL ---------------- */
const WATERFALL = [
    { label: "Prezzo rivendita", value: 184000, type: "start" },
    { label: "Acquisto", value: -118000 },
    { label: "Imposte", value: -9400 },
    { label: "Procedura", value: -2300 },
    { label: "Tecnici", value: -2100 },
    { label: "Ristrutturazione", value: -7900 },
    { label: "Condominio", value: -800 },
    { label: "Holding costs", value: -3100 },
    { label: "Agenzia", value: -4300 },
    { label: "Fiscalità", value: -3000 },
    { label: "Contingency", value: -1700 },
    { label: "UTILE NETTO", value: 31400, type: "end" },
];
const WF_MAX = 184000;

export const LandingWaterfall = () => (
    <section className="relative bg-[#0A0A0A] py-24 sm:py-32 border-t border-white/[0.06] lp-grain overflow-hidden" data-testid="landing-waterfall">
        <div className="max-w-[1080px] mx-auto px-6 md:px-10">
            <div className="max-w-[620px]">
                <Overline>COST BREAKDOWN</Overline>
                <motion.h2 {...fadeUp} className="lp-display font-extrabold text-white text-4xl sm:text-5xl leading-[1.02] tracking-tight">
                    Non guardiamo solo<br /><span className="text-white/45">il prezzo d'asta.</span>
                </motion.h2>
                <motion.p {...fadeUp} className="lp-body text-white/55 text-base leading-relaxed mt-6">
                    Imposte, procedura, tecnici, condominio, tempi di detenzione:
                    i costi che dimentichi sono quelli che si mangiano il margine.
                    Qui sono tutti in fila, uno per uno.
                </motion.p>
            </div>
            <div className="mt-12 rounded-2xl border border-white/[0.1] bg-[#0F0F0F] p-6 sm:p-8">
                <div className="space-y-2.5">
                    {WATERFALL.map((w, i) => {
                        const width = Math.max((Math.abs(w.value) / WF_MAX) * 100, 2.4);
                        const isStart = w.type === "start";
                        const isEnd = w.type === "end";
                        return (
                            <motion.div key={w.label} initial={{ opacity: 0, x: -14 }} whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true, margin: "-40px" }} transition={{ delay: i * 0.08, duration: 0.45 }}
                                        className={`flex items-center gap-4 ${isEnd ? "pt-3 mt-3 border-t border-white/[0.08]" : ""}`}
                                        data-testid={`waterfall-row-${i}`}>
                                <span className={`lp-mono text-[11px] tracking-[0.08em] w-36 sm:w-44 shrink-0 text-right ${isEnd ? "text-[#00E599] font-bold" : isStart ? "text-white/85" : "text-white/45"}`}>
                                    {w.label}
                                </span>
                                <div className="flex-1 h-6 relative">
                                    <motion.div initial={{ width: 0 }} whileInView={{ width: `${width}%` }}
                                                viewport={{ once: true, margin: "-40px" }}
                                                transition={{ delay: i * 0.08 + 0.15, duration: 0.7, ease: "easeOut" }}
                                                className={`h-full rounded-sm ${isEnd ? "bg-[#00E599]" : isStart ? "bg-white/85" : "bg-[#FF3B30]/55"}`} />
                                </div>
                                <span className={`lp-mono text-[12px] w-24 shrink-0 ${isEnd ? "text-[#00E599] font-bold" : isStart ? "text-white/85" : "text-white/50"}`}>
                                    {w.value < 0 ? "−" : ""}€{Math.abs(w.value).toLocaleString("it-IT")}
                                </span>
                            </motion.div>
                        );
                    })}
                </div>
                <div className="lp-mono text-[8px] tracking-[0.14em] text-white/25 mt-5">ESEMPIO DIMOSTRATIVO · VOCI E IMPORTI CALCOLATI SU OGNI ASTA REALE</div>
            </div>
        </div>
    </section>
);

/* ---------------- DOCUMENT INTELLIGENCE ---------------- */
const DOC_FACTS = [
    { label: "SUPERFICIE", value: "92 mq", tag: "DOC" },
    { label: "STATO OCCUPAZIONE", value: "Libero", tag: "DOC" },
    { label: "DIFFORMITÀ", value: "Sanabile · €2.100", tag: "DOC" },
    { label: "CATASTO", value: "A/2 · 3,5 vani", tag: "DOC" },
    { label: "VINCOLI", value: "Nessuno", tag: "DOC" },
    { label: "COSTI POTENZIALI", value: "€8.100", tag: "STIMA" },
    { label: "RISCHIO COMPLESSIVO", value: "Basso", tag: "STIMA", accent: true },
];

export const LandingDocs = () => (
    <section className="relative bg-[#050505] py-24 sm:py-32 border-t border-white/[0.06] lp-grain overflow-hidden" data-testid="landing-docs">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-14 items-center">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.8 }} className="relative order-2 lg:order-1">
                <div className="relative rounded-xl border border-white/[0.12] bg-[#F2F0EA] p-6 max-w-[400px] mx-auto shadow-[0_30px_90px_rgba(0,0,0,0.5)] overflow-hidden">
                    <div className="lp-scanline" />
                    <div className="flex items-center gap-2 mb-5">
                        <FilePdf size={18} className="text-red-700" />
                        <span className="lp-mono text-[10px] tracking-[0.1em] text-[#444444]">PERIZIA_RGE_61-2014.pdf · 74 pagine</span>
                    </div>
                    <div className="space-y-2.5">
                        {[92, 78, 96, 60, 88, 72, 94, 52, 84, 68, 90, 44].map((w, i) => (
                            <div key={i} className="h-2 rounded-full bg-gray-400/40" style={{ width: `${w}%` }} />
                        ))}
                    </div>
                    <div className="mt-5 space-y-2.5">
                        {[80, 64, 92].map((w, i) => (
                            <div key={i} className="h-2 rounded-full bg-gray-400/25" style={{ width: `${w}%` }} />
                        ))}
                    </div>
                </div>
            </motion.div>
            <div className="order-1 lg:order-2">
                <Overline>DOCUMENT INTELLIGENCE</Overline>
                <motion.h2 {...fadeUp} className="lp-display font-extrabold text-white text-4xl sm:text-5xl leading-[1.02] tracking-tight">
                    Dalla perizia<br /><span className="text-white/45">alla decisione.</span>
                </motion.h2>
                <motion.p {...fadeUp} className="lp-body text-white/55 text-base leading-relaxed mt-6 max-w-[420px]">
                    74 pagine di perizia. I dati che ti servono sono 7.
                    Il sistema li estrae, li etichetta e li porta dentro l'analisi.
                </motion.p>
                <div className="mt-8 space-y-2 max-w-[420px]">
                    {DOC_FACTS.map((f, i) => (
                        <motion.div key={f.label} initial={{ opacity: 0, x: 18 }} whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true, margin: "-40px" }} transition={{ delay: i * 0.09, duration: 0.45 }}
                                    className="flex items-center justify-between rounded-lg border border-white/[0.09] bg-white/[0.025] px-4 py-2.5"
                                    data-testid={`doc-fact-${i}`}>
                            <span className="lp-mono text-[10px] tracking-[0.16em] text-white/45">{f.label}</span>
                            <span className="flex items-center gap-2.5">
                                <span className={`lp-mono text-[12px] font-bold ${f.accent ? "text-[#00E599]" : "text-white/90"}`}>{f.value}</span>
                                <span className={`lp-mono text-[8px] tracking-[0.1em] px-1.5 py-0.5 rounded border ${f.tag === "DOC" ? "border-[#3B82F6]/40 text-[#3B82F6]" : "border-[#F5B800]/40 text-[#F5B800]"}`}>{f.tag}</span>
                            </span>
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    </section>
);

/* ---------------- TRUST (LIGHT) ---------------- */
const TRUST = [
    { title: "Documentato vs stimato", text: "Ogni valore è etichettato: viene dalla perizia, dal catasto o è una stima? Lo vedi sempre." },
    { title: "Confidence dichiarata", text: "L'analisi ti dice quanto sono solidi i suoi dati e cosa manca per essere certi." },
    { title: "Assunzioni esplicite", text: "Comparabili usati, ipotesi di rivendita, breakdown dei costi: tutto ispezionabile, niente scatole nere." },
];

export const LandingTrust = () => (
    <section className="bg-[#F7F7F5] py-24 sm:py-28" data-testid="landing-trust">
        <div className="max-w-[1080px] mx-auto px-6 md:px-10">
            <Overline light>TRASPARENZA</Overline>
            <motion.h2 {...fadeUp} className="lp-display font-extrabold text-[#0A0A0A] text-4xl sm:text-5xl leading-[1.02] tracking-tight max-w-[560px]">
                Ogni numero<br /><span className="text-black/40">deve avere una spiegazione.</span>
            </motion.h2>
            <motion.p {...fadeUp} className="lp-body text-black/55 text-base leading-relaxed mt-6 max-w-[520px]">
                Non vendiamo magia. Vendiamo velocità di analisi e numeri che puoi verificare.
            </motion.p>
            <div className="grid sm:grid-cols-3 gap-px bg-black/[0.08] border border-black/[0.08] rounded-xl overflow-hidden mt-12">
                {TRUST.map((t, i) => (
                    <motion.div key={t.title} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }} transition={{ delay: i * 0.1, duration: 0.5 }}
                                className="bg-[#FFFFFF] p-7" data-testid={`trust-card-${i}`}>
                        <div className="lp-mono text-[10px] tracking-[0.2em] text-emerald-700 mb-3">{String(i + 1).padStart(2, "0")}</div>
                        <h3 className="lp-display font-bold text-lg text-[#0A0A0A]">{t.title}</h3>
                        <p className="lp-body text-[13px] text-black/55 leading-relaxed mt-2">{t.text}</p>
                    </motion.div>
                ))}
            </div>
            {/* A chi serve — compact */}
            <motion.div {...fadeUp} className="mt-14 flex flex-wrap items-center gap-x-2 gap-y-2" data-testid="landing-audience">
                <span className="lp-mono text-[10px] tracking-[0.2em] text-black/40 mr-3">A CHI SERVE →</span>
                {["Investitori immobiliari", "Professionisti delle aste", "Agenzie immobiliari", "Società immobiliari"].map((a) => (
                    <span key={a} className="lp-body text-[13px] font-semibold text-black/70 border border-black/15 rounded-full px-4 py-1.5 bg-[#FFFFFF]">{a}</span>
                ))}
            </motion.div>
        </div>
    </section>
);

/* ---------------- FINAL CTA ---------------- */
export const LandingFinalCta = () => {
    const { user } = useAuth();
    return (
        <section className="relative bg-[#050505] overflow-hidden lp-grain" data-testid="landing-final-cta">
            <div className="absolute inset-0">
                <img src={NIGHT_BG} alt="" loading="lazy" className="w-full h-full object-cover opacity-75 lp-kenburns" />
                <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/35 to-[#050505]" />
            </div>
            {[{ t: "26%", l: "20%" }, { t: "38%", l: "72%" }, { t: "56%", l: "38%" }, { t: "44%", l: "55%" }].map((m, i) => (
                <span key={i} className="lp-marker absolute w-2 h-2 rounded-full bg-[#00E599] z-[6]" style={{ top: m.t, left: m.l }} />
            ))}
            <div className="relative z-10 max-w-[860px] mx-auto px-6 md:px-10 py-32 sm:py-44 text-center">
                <motion.h2 {...fadeUp} className="lp-display font-extrabold text-white text-4xl sm:text-6xl leading-[1.02] tracking-tight">
                    La prossima opportunità<br />potrebbe essere già all'asta.
                </motion.h2>
                <motion.p {...fadeUp} className="lp-body text-white/60 text-base sm:text-lg mt-6 max-w-[520px] mx-auto">
                    Lascia che Asta Intelligence la cerchi, la analizzi e ti mostri i numeri.
                </motion.p>
                <motion.div {...fadeUp} className="flex flex-wrap items-center justify-center gap-4 mt-10">
                    <Link to={user ? "/dashboard" : "/register"} data-testid="final-cta-primary"
                          className="group h-[54px] px-8 inline-flex items-center gap-2.5 bg-[#FFFFFF] text-[#0A0A0A] text-[15px] font-bold rounded-full hover:bg-[#00E599] transition-colors duration-300 active:scale-[0.97]">
                        Inizia ora <ArrowRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                    {!user && (
                        <Link to="/login" data-testid="final-cta-login"
                              className="h-[54px] px-7 inline-flex items-center border border-white/25 text-white/85 text-[15px] font-semibold rounded-full hover:border-white/70 hover:text-white transition-colors">
                            Accedi
                        </Link>
                    )}
                </motion.div>
            </div>
        </section>
    );
};

/* ---------------- FOOTER ---------------- */
export const LandingFooter = () => (
    <footer className="bg-[#050505] border-t border-white/[0.07]" data-testid="landing-footer">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-9 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="lp-mono text-[10px] tracking-[0.08em] text-white/30 text-center sm:text-left">
                © {new Date().getFullYear()} DYNAMIC GROUP S.R.L.S. · VIA MAGENTA 31, FIRENZE · P.IVA 07282260483
            </div>
            <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5">
                {LEGAL_LINKS.map((l) => (
                    <Link key={l.to} to={l.to} className="lp-body text-[11px] text-white/40 hover:text-white/80 transition-colors">
                        {l.label}
                    </Link>
                ))}
            </nav>
        </div>
    </footer>
);
